/**
 * IPAMVlans — /networks/vlans (estándar de listados del CMDB).
 *
 * Antes: tabla propia partida en una tarjeta por site, sin selector de columnas,
 * sin vistas, sin filtros y con el alta embebida encima de la tabla; el borrado
 * usaba `window.confirm` (fuera del estándar) y los iconos eran emojis.
 *
 * Ahora: `ListPage` da buscador, filtros, columnas, cebra, export, vistas y los
 * botones `[acciones propias] · Copiar · Editar · Eliminar`. La agrupación por
 * site sigue existiendo, pero como filtro de tipo interruptor que activa el
 * `groupBy` de `ListPage` (igual que en Bloques y Subredes).
 *
 * Botones propios de una VLAN:
 *  · **Ver subredes** — salta a Subredes con esa VLAN ya filtrada (`?vlanId=`).
 *  · **Copiar** — abre el alta con nombre «(copia)», site y descripción; el
 *    VLAN ID lo elige el usuario, que es único por empresa y site.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ListBulletIcon,
  Squares2X2Icon,
  TableCellsIcon,
  QueueListIcon,
} from '@heroicons/react/24/outline';
import IPAMLayout from './IPAMLayout';
import ListPage from './common/ListPage';
import api from '../services/api';
import { compareText } from '../utils/ipamSort';
import { useListView } from '../views/ViewsContext';
import '../styles/DetailView.css';   // `.dv-fieldset` (la ficha se apaga de golpe en lectura)

/* Rangos de VLAN ID: los tramos que se usan de verdad en planta. */
const ID_RANGES = {
  low:  { label: '1 – 100',      test: (id) => id >= 1 && id <= 100 },
  mid:  { label: '101 – 500',    test: (id) => id >= 101 && id <= 500 },
  high: { label: '501 – 1000',   test: (id) => id >= 501 && id <= 1000 },
  top:  { label: '1001 – 4094',  test: (id) => id >= 1001 && id <= 4094 },
};

export default function IPAMVlans({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  const [vlans, setVlans]     = useState([]);
  const [search, setSearch]   = useState('');
  const [filterSite, setFilterSite] = useState('');
  const [filterUse, setFilterUse]   = useState('');
  const [filterRange, setFilterRange] = useState('');
  const [filterDesc, setFilterDesc] = useState('');
  const [grouped, setGrouped]       = useState(true);
  const [hlId, setHlId]             = useState(null);
  const [knownSites, setKnownSites] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [initialMode, setInitialMode] = useState('create');   // create | view | edit
  const [editingVlan, setEditingVlan] = useState(null);
  const [cloneSource, setCloneSource] = useState(null);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const v = await api.getIpamVlans(companyId);
      setVlans(v.data || []);
      setKnownSites(prev => {
        const merged = new Set(prev);
        (v.data || []).forEach(row => { if (row.site) merged.add(row.site); });
        return [...merged].sort((a, b) => compareText(a, b));
      });
    } catch (e) {
      setError(e.message || 'Error cargando VLANs');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  /* ?destacar=<id>[&abrir=1] — convención del CMDB. */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !vlans.length) return undefined;
    const id = Number(raw);
    const target = vlans.find(v => v.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { openView(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`vlan-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, vlans, setSearchParams]);

  /* ── Ficha (lectura por defecto) ─────────────────────────────────────── */
  const openView = (vlan) => { setCloneSource(null); setEditingVlan(vlan); setInitialMode('view'); setFormOpen(true); };
  const openEdit = (vlan) => { setCloneSource(null); setEditingVlan(vlan); setInitialMode('edit'); setFormOpen(true); };
  const openCreate = () => { setCloneSource(null); setEditingVlan(null); setInitialMode('create'); setFormOpen(true); };

  const closeForm = () => {
    setFormOpen(false);
    setEditingVlan(null);
    setCloneSource(null);
    load();
  };

  /* ── Guardar (alta o edición, según lo que haya abierto) ─────────────── */
  const handleSave = async (data) => {
    setSaving(true);
    try {
      const editingId = (cloneSource || editingVlan)?.id;
      const isEdit = initialMode === 'edit' && editingId != null;
      if (isEdit) {
        await api.updateIpamVlan(editingId, data, companyId);
      } else {
        await api.createIpamVlan({ ...data, vlan_id: Number(data.vlan_id) }, companyId);
      }
      setSuccess(isEdit
        ? `VLAN ${data.vlan_id} actualizada.`
        : `VLAN ${data.vlan_id} creada.`);
      setFormOpen(false);
      setEditingVlan(null);
      setCloneSource(null);
      await load();
    } catch (e) {
      setError(e.message || 'Error guardando la VLAN');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vlan) => {
    try {
      await api.deleteIpamVlan(vlan.id, companyId);
      await load();
    } catch (e) {
      setError(e.message || 'Error eliminando VLAN');
    }
  };

  /** Copiar: precarga el alta; el VLAN ID hay que elegirlo (es único). */
  const handleClone = (vlan) => {
    if (!vlan) return;
    setCloneSource({
      name: vlan.name ? `${vlan.name} (copia)` : '',
      site: vlan.site || '',
      description: vlan.description || '',
      vlan_id: '',
    });
    setEditingVlan(null);
    setInitialMode('create');
    setFormOpen(true);
  };

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 9000);
    return () => clearTimeout(timer);
  }, [success]);

  /* ── Filtrado ─────────────────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const range = ID_RANGES[filterRange];
    return vlans.filter((v) => {
      if (filterSite && (v.site || '') !== filterSite) return false;
      if (range && !range.test(Number(v.vlan_id))) return false;
      if (filterUse === 'used' && !(v.subnets || []).length) return false;
      if (filterUse === 'free' && (v.subnets || []).length) return false;
      if (filterDesc === 'with' && !v.description) return false;
      if (filterDesc === 'without' && v.description) return false;
      if (!needle) return true;
      return String(v.vlan_id).includes(needle)
        || (v.name || '').toLowerCase().includes(needle)
        || (v.site || '').toLowerCase().includes(needle)
        || (v.description || '').toLowerCase().includes(needle);
    });
  }, [vlans, search, filterSite, filterUse, filterRange, filterDesc]);

  /** Etiqueta del grupo cuando se agrupa por site. Si el toggle está apagado
      no se pasa `groupBy` (devolver `null` creaba un grupo «Sin valor»). */
  const groupLabelOf = useCallback(
    (vlan) => vlan.site || tr('ipam.no_site', 'Sin site'),
    [tr],
  );

  /* ── Columnas (7 de catálogo; 6 visibles de entrada) ──────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'vlan_id',
        label: tr('vlan.id', 'VLAN'),
        sortable: true,
        render: (v) => <span className="list-badge">{tr('vlan.short', 'VLAN')} {v.vlan_id}</span>,
      },
      {
        key: 'name',
        label: tr('ipam.name', 'Nombre'),
        sortable: true,
        render: (v) => <span style={{ fontWeight: 600 }}>{v.name || dash}</span>,
      },
      {
        key: 'site',
        label: tr('ipam.site', 'Site'),
        sortable: true,
        sortValue: (v) => v.site || '',
        render: (v) => v.site || dash,
      },
      {
        key: 'subnet_count',
        label: tr('ipam.subnets', 'Subredes'),
        sortable: true,
        sortValue: (v) => (v.subnets || []).length,
        render: (v) => {
          const subs = v.subnets || [];
          if (!subs.length) return <span className="list-badge list-badge--off">{tr('vlan.free', 'Libre')}</span>;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="list-badge list-badge--on"
                style={{ cursor: 'pointer' }}
                title={tr('vlan.view_subnets', 'Ver las subredes con esta VLAN')}
                onClick={(event) => { event.stopPropagation(); navigate(`/networks/subnets?vlanId=${v.id}`); }}
              >
                {subs.length}
              </button>
              {subs.map(s => (
                <span key={s.id} className="list-code" title={s.name || ''}>{s.cidr}</span>
              ))}
            </span>
          );
        },
      },
      {
        key: 'description',
        label: tr('description', 'Descripción'),
        sortable: true,
        render: (v) => v.description || dash,
      },
      {
        key: 'created_at',
        label: tr('vlan.created', 'Creada'),
        sortable: true,
        sortValue: (v) => v.created_at || '',
        render: (v) => (v.created_at
          ? <span className="list-hint">{String(v.created_at).slice(0, 10)}</span>
          : dash),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, navigate]);

  const defaultVisible = ['vlan_id', 'name', 'site', 'subnet_count', 'description'];

  const filters = [
    {
      key: 'site', label: tr('ipam.site', 'Site'), allLabel: tr('ipam.site_all', 'Todos los sites'),
      value: filterSite, onChange: setFilterSite,
      options: knownSites.map(s => ({ value: s, label: s })),
    },
    {
      key: 'use', label: tr('vlan.use', 'Uso'), allLabel: tr('vlan.use_all', 'Con y sin subredes'),
      value: filterUse, onChange: setFilterUse,
      options: [
        { value: 'used', label: tr('vlan.use_used', 'Con subredes') },
        { value: 'free', label: tr('vlan.use_free', 'Sin subredes (libre)') },
      ],
    },
    {
      key: 'range', label: tr('vlan.range', 'Rango'), allLabel: tr('vlan.range_all', 'Cualquier VLAN ID'),
      value: filterRange, onChange: setFilterRange,
      options: Object.entries(ID_RANGES).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'desc', label: tr('description', 'Descripción'), allLabel: tr('vlan.desc_all', 'Con y sin descripción'),
      value: filterDesc, onChange: setFilterDesc,
      options: [
        { value: 'with', label: tr('vlan.desc_with', 'Con descripción') },
        { value: 'without', label: tr('vlan.desc_without', 'Sin descripción') },
      ],
    },
    {
      key: 'group', label: tr('ipam.group_site', 'Agrupar por site'),
      type: 'toggle', value: grouped, onValue: true,
      onChange: (value) => setGrouped(Boolean(value)),
    },
  ];

  const subtitle = `${vlans.length} ${tr('vlan.plural', 'VLANs')}`
    + ` · ${filtered.length} ${tr('location.filtered', 'filtradas')}`;

  const exportRows = useMemo(() => filtered.map(v => ({
    'VLAN ID': v.vlan_id,
    'Nombre': v.name || '',
    'Site': v.site || '',
    'Subredes': (v.subnets || []).map(s => s.cidr).join(', '),
    'Nº de subredes': (v.subnets || []).length,
    'Descripción': v.description || '',
  })), [filtered]);

  /** Tarjetas: lo esencial de la VLAN, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((v) => {
          const subs = v.subnets || [];
          return (
            <div
              key={v.id}
              id={`vlan-card-${v.id}`}
              className={`list-card${v.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => openView(v)}
              onKeyDown={(event) => { if (event.key === 'Enter') openView(v); }}
              title={`${tr('action.view', 'Ver')} VLAN ${v.vlan_id}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title list-code">VLAN {v.vlan_id}</div>
                  {v.name && <span>{v.name}</span>}
                </div>
                {!subs.length && <span className="list-badge list-badge--off">{tr('vlan.free', 'Libre')}</span>}
              </div>

              <div className="list-card-meta">
                {v.site && <span>📍 {v.site}</span>}
                {subs.length > 0 && <span>{subs.length} {tr('ipam.subnets', 'subredes').toLowerCase()}</span>}
                {subs.map(s => (
                  <span key={s.id} className="list-code" title={s.name || ''}>{s.cidr}</span>
                ))}
              </div>

              {v.description && <div className="list-card-meta"><span>{v.description}</span></div>}

              <div className="list-card-actions">{rowActions(v)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  const VLAN_VIEWS = useMemo(() => [
    { key: 'list', label: tr('ipam.view_list', 'Lista'), icon: ListBulletIcon, title: tr('ipam.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('ipam.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('ipam.view_cards_hint', 'Tarjetas con lo esencial de cada VLAN'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId]);
  useListView('ipam_vlans', 'list');

  if (!companyId) {
    return (
      <IPAMLayout>
        <div className="alert alert-warning">{tr('ipam.select_company', 'Selecciona una empresa para ver el IPAM.')}</div>
      </IPAMLayout>
    );
  }

  return (
    <IPAMLayout>
      {error && (
        <div className="alert alert-danger py-2 d-flex justify-content-between">
          <span>{error}</span>
          <button className="btn-close" onClick={() => setError('')} />
        </div>
      )}
      {success && (
        <div className="alert alert-success py-2 d-flex justify-content-between">
          <span>{success}</span>
          <button className="btn-close" onClick={() => setSuccess('')} />
        </div>
      )}

      <div style={{ display: formOpen ? 'none' : undefined }}>
        <ListPage
          icon={TableCellsIcon}
          title={tr('vlan.title', 'VLANs')}
          subtitle={subtitle}

          onCreate={openCreate}
          createLabel={tr('vlan.new', 'Nueva VLAN')}
          createTitle={tr('vlan.new_hint', 'Crear una VLAN y asignarla después a sus subredes')}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('vlan.search', 'Buscar VLAN ID, nombre, site…')}
          filters={filters}

          exportRows={exportRows}
          exportFileBase="vlans"
          exportSheetName="VLANs"

          columns={columns}
          columnStorageKey="cmdb_cols_ipam_vlans"
          defaultVisible={defaultVisible}
          rows={filtered}
          rowKey={(v) => v.id}
          rowId={(v) => `vlan-row-${v.id}`}
          rowClassName={(v) => (v.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={openView}
          rowTitle={(v) => `${tr('action.view', 'Ver')} VLAN ${v.vlan_id}`}
          groupBy={grouped ? groupLabelOf : undefined}

          views={VLAN_VIEWS}
          listKey="ipam_vlans"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando VLANs…')}

          empty={{
            icon: '🏷️',
            title: vlans.length === 0
              ? tr('vlan.empty', 'No hay VLANs')
              : tr('msg.no_results', 'Ninguna VLAN coincide con los filtros'),
            subtitle: vlans.length === 0
              ? tr('vlan.empty_hint', 'Crea la primera para poder asignarla a las subredes.')
              : undefined,
            action: vlans.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={openCreate}>
                ＋ {tr('vlan.new', 'Nueva VLAN')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (v) => (
              <button
                type="button"
                className="list-action-btn"
                disabled={!(v.subnets || []).length}
                onClick={() => navigate(`/networks/subnets?vlanId=${v.id}`)}
                title={tr('vlan.view_subnets', 'Ver las subredes con esta VLAN')}
              >
                <QueueListIcon />
              </button>
            ),
            onCopy: handleClone,
            onEdit: openEdit,
            onDelete: handleDelete,
            deleteTitle: tr('vlan.delete_title', 'Eliminar VLAN'),
            deleteMessage: (v) => (
              <>
                {tr('vlan.delete_confirm', '¿Seguro que quieres eliminar la VLAN')} <strong>{v.vlan_id}</strong>
                {v.name ? ` (${v.name})` : ''}?{' '}
                { (v.subnets || []).length > 0
                  ? tr('vlan.delete_warn_used', 'Ojo: está asignada a subredes, que se quedarán sin VLAN. No se puede deshacer.')
                  : tr('vlan.delete_warn', 'No se puede deshacer.') }
              </>
            ),
          }}
        />
      </div>

      {/* Alta (modal estándar), ficha en lectura y edición */}
      {formOpen && (
        <VlanForm
          vlan={cloneSource || editingVlan}
          mode={cloneSource ? 'create' : initialMode}
          saving={saving}
          onSave={handleSave}
          onClose={closeForm}
        />
      )}
    </IPAMLayout>
  );
}

/* ── Ficha / alta / edición de una VLAN ──────────────────────────────────────
   Un solo modal con tres modos:
     create → los cuatro campos vacíos (o precargados al Copiar)
     view   → se entra en LECTURA (es lo que abre el clic en la fila)
     edit   → se edita
────────────────────────────────────────────────────────────────────────────── */
function VlanForm({ vlan, mode = 'create', saving, onSave, onClose }) {
  const { t } = useTranslation();
  const tr = (key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };
  const [editing, setEditing] = useState(mode !== 'view');
  const [form, setForm] = useState({
    vlan_id:     vlan?.vlan_id != null ? String(vlan.vlan_id) : '',
    name:        vlan?.name        || '',
    site:        vlan?.site        || '',
    description: vlan?.description || '',
  });

  const isExisting = vlan?.id != null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...form, vlan_id: Number(form.vlan_id) });
  };

  return (
    <div
      className="modal d-block"
      style={{ background: 'rgba(0,0,0,0.45)', zIndex: 1060 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header py-2">
            <h6 className="modal-title fw-semibold mb-0">
              {!isExisting
                ? tr('vlan.new', 'Nueva VLAN')
                : (editing ? tr('vlan.edit', 'Editar VLAN') : tr('vlan.view', 'VLAN'))}
              {isExisting && <span className="list-badge ms-2">VLAN {vlan.vlan_id}</span>}
            </h6>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={handleSubmit}>
            <fieldset disabled={!editing} className="dv-fieldset">
              <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label className="form-label small mb-1">{tr('vlan.id', 'VLAN ID')}</label>
                  <input className="form-control form-control-sm" type="number" min="1" max="4094" required
                    value={form.vlan_id} onChange={e => setForm(f => ({ ...f, vlan_id: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label small mb-1">{tr('ipam.name', 'Nombre')}</label>
                  <input className="form-control form-control-sm" required
                    value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label small mb-1">{tr('ipam.site', 'Site')}</label>
                  <input className="form-control form-control-sm" placeholder="datacenter-1"
                    value={form.site} onChange={e => setForm(f => ({ ...f, site: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label small mb-1">{tr('description', 'Descripción')}</label>
                  <input className="form-control form-control-sm"
                    value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </div>

                {editing && (form.vlan_id || '').toString() && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span className="list-hint">
                      {tr('vlan.unique_hint', 'El VLAN ID es único por empresa y site: si ya existe en ese site, el alta dará error.')}
                    </span>
                  </div>
                )}

                {!editing && vlan?.subnets?.length > 0 && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <span className="list-hint">
                      {tr('vlan.used_by', 'Subredes con esta VLAN')}: {vlan.subnets.map(s => s.cidr).join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </fieldset>
            <div className="modal-footer py-2">
              {editing ? (
                <>
                  <button type="button" className="list-btn-secondary" onClick={onClose}>
                    {tr('action.cancel', 'Cancelar')}
                  </button>
                  <button type="submit" className="list-btn-primary" disabled={saving}>
                    {saving ? tr('msg.saving', 'Guardando…') : tr('action.save', 'Guardar')}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" className="list-btn-secondary" onClick={onClose}>
                    {tr('action.close', 'Cerrar')}
                  </button>
                  <button type="button" className="list-btn-primary" onClick={() => setEditing(true)}>
                    {tr('action.edit', 'Editar')}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
